import { api } from "../api";

// Sort dimensions in actual fiscal order rather than alphabetically.
// Shared copy of the logic in BudgetOverviewView.tsx — kept in one place so
// every dimension picker (download modals, Budget Overview, etc.) agrees on
// ordering instead of drifting independently.
const DIM_TYPE_ORDER: Record<string, number> = {
  "annual budget": 0, "july forecast": 1, "october forecast": 2, "january forecast": 3,
};
export function sortDims(dims: string[]): string[] {
  const parse = (d: string) => {
    const fyMatch = d.match(/FY(\d+)/i);
    const fy = fyMatch ? parseInt(fyMatch[1], 10) : 0;
    const roundMatch = d.match(/Planning Round (\d+)/i);
    if (roundMatch) return { fy, rank: 4 + parseInt(roundMatch[1], 10) };
    const typeKey = d.toLowerCase().replace(/\s*fy\d+/i, "").trim();
    return { fy, rank: DIM_TYPE_ORDER[typeKey] ?? 99 };
  };
  return [...dims].sort((a, b) => {
    const pa = parse(a), pb = parse(b);
    return pa.fy !== pb.fy ? pa.fy - pb.fy : pa.rank - pb.rank;
  });
}

// Filename-safe abbreviation for any dimension string — mirrors
// backend/routes/export.js's abbreviateDimension exactly, so the preview
// filename shown before download matches what's actually downloaded.
const DIM_ABBREV: Record<string, string> = {
  "Annual Budget": "ab", "July Forecast": "jff", "October Forecast": "off", "January Forecast": "janff",
};
export function abbreviateDimension(fullDimStr: string): string {
  const roundMatch = fullDimStr.match(/^FY(\d+)\s+Planning Round\s+(\d+)$/i);
  if (roundMatch) return `fy${roundMatch[1]}r${roundMatch[2]}`;
  const fyMatch = fullDimStr.match(/\s+(FY\d+)$/i);
  const fySuffix = fyMatch ? fyMatch[1].toLowerCase() : "";
  const baseDim = fullDimStr.replace(/\s+FY\d+$/i, "").trim();
  const abbrev = DIM_ABBREV[baseDim] || baseDim.toLowerCase().replace(/[^a-z0-9]+/g, "").slice(0, 10) || "dim";
  return fySuffix ? `${abbrev}-${fySuffix}` : abbrev;
}

export interface ExportDimensionOptions {
  options: string[];
  defaultDimension: string;
}

/**
 * Every dimension a cost center has budget data in, fully FY-qualified
 * (e.g. "Annual Budget FY26", "FY27 Planning Round 1"), fiscal-ordered, with
 * the currently-open cycle picked as the default — shared by every
 * "download budget sheet" feature so admin and cost-center-owner views stay
 * consistent instead of each hardcoding its own 4-item dimension list.
 */
export async function fetchExportDimensionOptions(cc: string): Promise<ExportDimensionOptions> {
  const [dimsRes, openRes] = await Promise.all([
    api.get("/budgets/compare/dimensions", { params: { cc } }),
    api.get("/budgets/open-dimension", { params: { cc } }).catch(() => ({ data: { dimension: null } })),
  ]);
  const options = sortDims(dimsRes.data?.dimensions || []);
  const openDim: string | null = openRes.data?.dimension || null;
  const defaultDimension = (openDim && options.includes(openDim))
    ? openDim
    : options[options.length - 1] || "";
  return { options, defaultDimension };
}
